import React, { useState } from "react";
import {
  Card,
  Spinner,
  Dropdown,
  OverlayTrigger,
  Tooltip,
} from "react-bootstrap";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faStore,
  faBuilding,
  faMapMarkerAlt,
  faLock,
  faUnlock,
  faBriefcase,
  faEllipsisVertical,
} from "@fortawesome/free-solid-svg-icons";
import { useFormContext, useWatch } from "react-hook-form";
import { EmpresaType, FormValues } from "./userTypes";
import { useEmpresas } from "providers/EmpresaProvider";
import AccessDeniedOverlay from "components/modules/common/AccessDeniedOverlay";

interface LojasAssociadasProps {
  isDeleted?: boolean;
}

const LojasAssociadas: React.FC<LojasAssociadasProps> = ({ isDeleted }) => {
  const { setValue, getValues } = useFormContext<FormValues>();
  const [openDropdownId, setOpenDropdownId] = useState<number | null>(null);
  const { empresas, isLoading } = useEmpresas();
  
  // Get current storeAccess from form context
  const storeAccess = useWatch({ name: "storeAccess" });

  // Helper functions moved from parent component
  const getStoreAccess = (empresaId: number) => {
    return (
      storeAccess?.find((sa: any) => sa.empresaId === empresaId) || {
        empresaId,
        hasAccess: false,
        isWorkLocation: false,
      }
    );
  };

  const updateStoreAccess = (
    empresaId: number,
    hasAccess: boolean,
    isWorkLocation: boolean = false
  ) => {
    // Get current storeAccess from form
    const currentStoreAccess = getValues("storeAccess") || [];
    let newStoreAccess = [...currentStoreAccess];

    if (isWorkLocation) {
      // If setting a new work location, remove that flag from all others
      newStoreAccess = newStoreAccess.map((sa) => ({
        ...sa,
        isWorkLocation: sa.empresaId === empresaId ? isWorkLocation : false,
      }));
    }

    const existingIndex = newStoreAccess.findIndex(
      (sa) => sa.empresaId === empresaId
    );
    if (existingIndex >= 0) {
      // Update existing entry
      newStoreAccess[existingIndex] = {
        empresaId,
        hasAccess,
        isWorkLocation:
          isWorkLocation || newStoreAccess[existingIndex].isWorkLocation,
      };
    } else {
      // Add new entry
      newStoreAccess.push({ empresaId, hasAccess, isWorkLocation });
    }

    // Update form values directly
    setValue("storeAccess", newStoreAccess, { shouldDirty: true });

    // Update empresasAssociadas with the correct format
    setValue(
      "empresasAssociadas",
      newStoreAccess
        .filter((sa) => sa.hasAccess)
        .map((sa) => ({
          empresaId: sa.empresaId,
          local: sa.isWorkLocation,
        })),
      { shouldDirty: true }
    );
  };

  const toggleStoreAccess = (empresaId: number) => {
    const access = getStoreAccess(empresaId);
    updateStoreAccess(
      empresaId,
      !access.hasAccess,
      access.isWorkLocation && !access.hasAccess
    );
  };

  const setAsWorkLocation = (empresaId: number) => {
    updateStoreAccess(empresaId, true, true);
  };

  const toggleDropdown = (empresaId: number, e?: React.MouseEvent) => {
    if (e) {
      e.stopPropagation();
    }
    setOpenDropdownId(openDropdownId === empresaId ? null : empresaId);
  };

  const formatCnpj = (cnpj: string) => {
    if (!cnpj) return "";
    return cnpj.replace(
      /^(\d{2})(\d{3})(\d{3})(\d{4})(\d{2})$/,
      "$1.$2.$3/$4-$5"
    );
  };

  return (
    <Card className={`mb-4`}>
      <div className={`${isDeleted ? "position-relative" : ""}`}>
        {isDeleted && (
          <div
            className="position-absolute top-0 start-0 h-100"
            style={{
              width: "4px",
              backgroundColor: "#dc3545",
              borderTopLeftRadius: "4px",
              borderBottomLeftRadius: "4px",
            }}
          ></div>
        )}
        <Card.Body>
          <h5 className="mb-3">
            <FontAwesomeIcon icon={faStore} className="me-2" />
            Lojas Associadas
          </h5>

          {isLoading ? (
            <div className="text-center py-4">
              <Spinner
                animation="border"
                role="status"
                size="sm"
                className="me-2"
              />
              <span>Carregando lojas...</span>
            </div>
          ) : !empresas || empresas.length === 0 ? (
            <AccessDeniedOverlay 
              message="Você não tem permissão para consultar ou visualizar lojas."
              compact
            >
              <div className="alert alert-info">
                Nenhuma loja disponível no momento.
              </div>
            </AccessDeniedOverlay>
          ) : (
            <div
              className={`row row-cols-1 row-cols-md-2 row-cols-lg-3 g-4 ${
                isDeleted ? "opacity-75" : ""
              }`}
            >
              {empresas.map((empresa) => {
                const access = getStoreAccess(empresa.id);
                const hasAccess = access.hasAccess;
                const isCurrentWorkLocation = access.isWorkLocation;
                const isDropdownOpen = openDropdownId === empresa.id;

                return (
                  <div className="col" key={empresa.id}>
                    <div
                      className={`card h-100 border ${
                        hasAccess
                          ? isCurrentWorkLocation
                            ? "border-success"
                            : "border-primary"
                          : "border-light"
                      } position-relative ${
                        isDeleted ? "bg-gray-100" : ""
                      }`}
                      style={{
                        transition: "all 0.2s ease-in-out",
                        transform:
                          hasAccess && !isDeleted
                            ? "scale(1.02)"
                            : "scale(1)",
                        cursor: isDeleted ? "default" : "pointer",
                        opacity: isDeleted ? 0.8 : 1,
                        overflow: "visible",
                      }}
                      onClick={(e) =>
                        isDeleted
                          ? e.preventDefault()
                          : toggleDropdown(empresa.id, e)
                      }
                    >
                      <Dropdown
                        className="position-absolute"
                        style={{ top: "10px", right: "10px" }}
                        show={isDropdownOpen && !isDeleted}
                        onToggle={() =>
                          !isDeleted && toggleDropdown(empresa.id)
                        }
                      >
                        <Dropdown.Toggle
                          as="div"
                          className={`btn btn-sm btn-light rounded-circle ${
                            isDeleted ? "d-none" : ""
                          }`}
                          style={{
                            width: "32px",
                            height: "32px",
                            padding: "0",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            visibility:
                              isDropdownOpen && !isDeleted
                                ? "visible"
                                : "hidden",
                          }}
                          onClick={(e) =>
                            isDeleted
                              ? e.preventDefault()
                              : e.stopPropagation()
                          }
                        >
                          <FontAwesomeIcon icon={faEllipsisVertical} />
                        </Dropdown.Toggle>

                        <Dropdown.Menu
                          align="end"
                          onClick={(e) =>
                            isDeleted
                              ? e.preventDefault()
                              : e.stopPropagation()
                          }
                        >
                          <Dropdown.Item
                            onClick={(e) => {
                              if (isDeleted) return;
                              e.stopPropagation();
                              toggleStoreAccess(empresa.id);
                              setOpenDropdownId(null);
                            }}
                          >
                            <FontAwesomeIcon
                              icon={hasAccess ? faLock : faUnlock}
                              className={`me-2 ${
                                hasAccess ? "text-danger" : "text-primary"
                              }`}
                            />
                            {hasAccess
                              ? "Remover acesso"
                              : "Permitir acesso"}
                          </Dropdown.Item>

                          <Dropdown.Item
                            onClick={(e) => {
                              if (isDeleted) return;
                              e.stopPropagation();
                              setAsWorkLocation(empresa.id);
                              setOpenDropdownId(null);
                            }}
                            disabled={isCurrentWorkLocation}
                          >
                            <FontAwesomeIcon
                              icon={faBriefcase}
                              className="me-2 text-success"
                            />
                            {isCurrentWorkLocation
                              ? "Local de trabalho atual"
                              : "Definir como local de trabalho"}
                          </Dropdown.Item>
                        </Dropdown.Menu>
                      </Dropdown>

                      {hasAccess && (
                        <div
                          className={`position-absolute d-flex justify-content-center align-items-center ${
                            isCurrentWorkLocation
                              ? "bg-success"
                              : "bg-primary"
                          } ${isDeleted ? "opacity-50" : ""}`}
                          style={{
                            top: "-10px",
                            left: "-10px",
                            width: "28px",
                            height: "28px",
                            borderRadius: "50%",
                            color: "white",
                          }}
                        >
                          <FontAwesomeIcon
                            icon={
                              isCurrentWorkLocation
                                ? faBriefcase
                                : faUnlock
                            }
                            size="sm"
                          />
                        </div>
                      )}

                      <div className="card-body d-flex flex-column">
                        <div className="d-flex align-items-center mb-3">
                          <div
                            className={`d-flex justify-content-center align-items-center me-3 ${
                              hasAccess && !isDeleted
                                ? ""
                                : "text-muted"
                            }`}
                            style={{
                              width: "40px",
                              height: "40px",
                              backgroundColor: "#f8f9fa",
                              borderRadius: "8px",
                            }}
                          >
                            <FontAwesomeIcon
                              icon={hasAccess ? faBuilding : faLock}
                              size="lg"
                            />
                          </div>
                          <div>
                            <h6 className={"fw-bold mb-0"}>
                              {empresa.nomeFantasia}
                              {isCurrentWorkLocation && (
                                <OverlayTrigger
                                  placement="top"
                                  overlay={
                                    <Tooltip>Local de trabalho</Tooltip>
                                  }
                                >
                                  <span
                                    className={`ms-2 badge ${
                                      isDeleted
                                        ? "bg-secondary"
                                        : "bg-success"
                                    }`}
                                  >
                                    <FontAwesomeIcon
                                      icon={faBriefcase}
                                      size="xs"
                                    />
                                  </span>
                                </OverlayTrigger>
                              )}
                            </h6>
                            <small>{formatCnpj(empresa.cnpj)}</small>
                          </div>
                        </div>

                        <div className={`mt-2 pt-2 border-top`}>
                          <div className="d-flex align-items-start">
                            <FontAwesomeIcon
                              icon={faMapMarkerAlt}
                              className={`mt-1 me-2 ${
                                hasAccess && !isDeleted
                                  ? "text-primary"
                                  : "text-secondary"
                              }`}
                            />
                            <div>
                              <p className="mb-1 small">
                                {empresa.endereco?.logradouro},{" "}
                                {empresa.endereco?.numero}
                                {empresa.endereco?.complemento
                                  ? ` - ${empresa.endereco?.complemento}`
                                  : ""}
                              </p>
                              <p className="mb-1 small">
                                {empresa.endereco?.bairro}
                              </p>
                              <p className={`mb-0 small`}>
                                {empresa.endereco?.municipio} -{" "}
                                {empresa.endereco?.uf},{" "}
                                {empresa.endereco?.cep}
                              </p>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </Card.Body>
      </div>
    </Card>
  );
};

export default LojasAssociadas;
